// Settings → Google. Every Google account the org uses, in one place.
//
// This merges what used to be three separate screens: the "My Account" tab,
// the "Email" tab's Google connect, and each project's Connections tab. They
// are the same thing at different scopes, so they're now rows in one list:
//
//   Company-wide  → one account everyone falls back to
//   My account    → the signed-in user's own (a personal Gmail is fine)
//   Per project   → one account per project
//
// Accounts connected through the old screens show up here automatically: the
// backend falls back to reading the legacy collections, so nothing needs
// reconnecting.

import React, { useMemo, useState } from "react";
import {
  Building2, UserCircle, FolderKanban, CheckCircle2, AlertTriangle,
  Loader2, LogOut, Plus,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../../atoms";
import Badge from "../../atoms/Badge";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";
import {
  useGoogleConnections, useStartGoogleConnect, useDisconnectGoogle,
} from "../../hooks/useGoogleConnections";
import { useProjects } from "../../hooks/useProjects";

const SCOPE_META = {
  org: {
    icon: Building2,
    title: "Company-wide account",
    hint: "Used by anyone in the org who hasn't connected their own account.",
  },
  user: {
    icon: UserCircle,
    title: "My Google account",
    hint: "Meetings you schedule land on this calendar. A personal Gmail works here.",
  },
  project: {
    icon: FolderKanban,
    title: "Project account",
    hint: "Used for leads in this project.",
  },
};

// Precedence is a product rule, not a detail — state it once at the top so
// nobody has to guess which account a meeting will actually use.
function PrecedenceNote() {
  return (
    <p style={{
      margin: "0 0 16px", fontSize: 12, lineHeight: 1.6,
      color: "var(--color-text-secondary)",
    }}>
      When you schedule a meeting we use <strong>your own account</strong> if you've
      connected one, otherwise the <strong>project's</strong>, otherwise the{" "}
      <strong>company-wide</strong> one.
    </p>
  );
}

function ConnectionRow({ meta, label, connection, onConnect, onDisconnect, busy }) {
  const Icon = meta.icon;
  const connected = !!connection?.connected;
  const needsReauth = connection?.status === "reauth_required";

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 14px",
      borderBottom: "1px solid var(--color-border-subtle)",
    }}>
      <Icon size={16} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
            {label}
          </span>
          {connected && !needsReauth && (
            <Badge tone="success"><CheckCircle2 size={10} /> Connected</Badge>
          )}
          {needsReauth && (
            <Badge tone="danger"><AlertTriangle size={10} /> Reconnect needed</Badge>
          )}
        </div>
        <div style={{
          fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {connected ? connection.email : meta.hint}
        </div>
      </div>

      {connected ? (
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {needsReauth && (
            <Button size="sm" onClick={onConnect} disabled={busy}>Reconnect</Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<LogOut size={12} />}
            onClick={onDisconnect}
            disabled={busy}
          >
            Disconnect
          </Button>
        </div>
      ) : (
        <Button size="sm" leftIcon={<Plus size={12} />} onClick={onConnect} disabled={busy}>
          Connect
        </Button>
      )}
    </div>
  );
}

export default function GoogleConnectionsSection({ isAdmin = false }) {
  const { projects = [] } = useProjects();
  const projectIds = useMemo(() => projects.map((p) => String(p._id)), [projects]);

  const { data: connections = [], isLoading, isError, error } = useGoogleConnections(projectIds);
  const startMut = useStartGoogleConnect();
  const disconnectMut = useDisconnectGoogle();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const byKey = useMemo(() => {
    const map = new Map();
    for (const c of connections) map.set(`${c.scope}:${c.scopeId}`, c);
    return map;
  }, [connections]);

  const handleConnect = async ({ scope, projectId }) => {
    try {
      setBusy(true);
      const res = await startMut.mutateAsync({ scope, projectId });
      if (!res?.authUrl) throw new Error("No auth URL returned");
      // Same-tab redirect; Google sends the user back to Settings afterwards.
      window.location.href = res.authUrl;
    } catch (err) {
      setBusy(false);
      toast.error(err?.response?.data?.error || err.message || "Couldn't start Google connect");
    }
  };

  const handleDisconnect = async () => {
    if (!confirm) return;
    try {
      await disconnectMut.mutateAsync({ scope: confirm.scope, projectId: confirm.projectId });
      toast.success("Google account disconnected");
      setConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to disconnect");
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 16, fontSize: 13, color: "var(--color-text-secondary)" }}>
        <Loader2 size={14} className="animate-spin" /> Loading Google connections…
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 16, fontSize: 13, color: "var(--crm-danger)" }}>
        Couldn't load Google connections: {error?.response?.data?.error || error?.message}
      </div>
    );
  }

  const orgConn = connections.find((c) => c.scope === "org");
  const userConn = connections.find((c) => c.scope === "user");

  return (
    <section style={{
      background: "var(--color-bg-surface)",
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8,
      overflow: "hidden",
      marginBottom: 16,
    }}>
      <header style={{ padding: "14px 14px 0" }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)" }}>
          Google accounts
        </h3>
        <p style={{ margin: "4px 0 10px", fontSize: 12, color: "var(--color-text-secondary)" }}>
          Calendar, Meet links and meeting recordings all use these.
        </p>
        <PrecedenceNote />
      </header>

      <ConnectionRow
        meta={SCOPE_META.user}
        label={SCOPE_META.user.title}
        connection={userConn}
        busy={busy}
        onConnect={() => handleConnect({ scope: "user" })}
        onDisconnect={() => setConfirm({ scope: "user", label: "your Google account" })}
      />

      {/* Company-wide is org config, so only admins can change it — but everyone
          sees whether one exists, since it's their fallback. */}
      {isAdmin ? (
        <ConnectionRow
          meta={SCOPE_META.org}
          label={SCOPE_META.org.title}
          connection={orgConn}
          busy={busy}
          onConnect={() => handleConnect({ scope: "org" })}
          onDisconnect={() => setConfirm({ scope: "org", label: "the company-wide Google account" })}
        />
      ) : (
        <div style={{
          padding: "12px 14px", borderBottom: "1px solid var(--color-border-subtle)",
          fontSize: 12, color: "var(--color-text-secondary)",
        }}>
          {orgConn?.connected
            ? `Company-wide account: ${orgConn.email}`
            : "No company-wide Google account is connected."}
        </div>
      )}

      {projects.length > 0 && (
        <div style={{ padding: "12px 14px 4px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--color-text-secondary)" }}>
          Projects
        </div>
      )}
      {projects.map((p) => (
        <ConnectionRow
          key={p._id}
          meta={SCOPE_META.project}
          label={p.name}
          connection={byKey.get(`project:${p._id}`)}
          busy={busy}
          onConnect={() => handleConnect({ scope: "project", projectId: p._id })}
          onDisconnect={() => setConfirm({
            scope: "project",
            projectId: p._id,
            label: `the Google account for ${p.name}`,
          })}
        />
      ))}

      <ConfirmDeleteModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleDisconnect}
        title="Disconnect Google account"
        message={`Disconnect ${confirm?.label || "this account"}? Meetings already scheduled stay on the Google calendar, but TeamGrid won't be able to sync or cancel them until an account is reconnected.`}
        confirmLabel="Disconnect"
        isPending={disconnectMut.isPending}
      />
    </section>
  );
}
